import type {
  ActivateInvitationRequest,
  ActivateInvitationResponse,
  ActivateMfaRequest,
  ActivateMfaResponse,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  RefreshRequest,
  RefreshResponse,
  RequestMfaEmailOtpRequest,
  RequestMfaEmailOtpResponse,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  SetupMfaRequest,
  SetupMfaResponse,
  VerifyMfaEmailOtpRequest,
  VerifyMfaEmailOtpResponse,
  VerifyMfaRequest,
  VerifyMfaResponse,
} from '@jq4race/shared';

import { request } from './client.js';

export type {
  ActivateInvitationRequest,
  ActivateInvitationResponse,
  ActivateMfaRequest,
  ActivateMfaResponse,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  RefreshRequest,
  RefreshResponse,
  RequestMfaEmailOtpRequest,
  RequestMfaEmailOtpResponse,
  SetupMfaRequest,
  SetupMfaResponse,
  VerifyMfaEmailOtpRequest,
  VerifyMfaEmailOtpResponse,
  VerifyMfaRequest,
  VerifyMfaResponse,
};

export const authApi = {
  login(input: LoginRequest): Promise<LoginResponse> {
    return request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  activate(input: ActivateInvitationRequest): Promise<ActivateInvitationResponse> {
    return request<ActivateInvitationResponse>('/auth/activate-invitation', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  refresh(input: RefreshRequest, opts?: { signal?: AbortSignal }): Promise<RefreshResponse> {
    return request<RefreshResponse>('/auth/refresh', {
      method: 'POST',
      body: input,
      refreshOn401: false,
      signal: opts?.signal,
    });
  },

  logout(input: LogoutRequest): Promise<void> {
    return request<void>('/auth/logout', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  requestPasswordReset(email: RequestPasswordResetRequest['email']): Promise<void> {
    return request<void>('/auth/request-password-reset', {
      method: 'POST',
      body: { email },
      refreshOn401: false,
    });
  },

  resetPassword(input: ResetPasswordRequest): Promise<void> {
    return request<void>('/auth/reset-password', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  setupMfa(input: SetupMfaRequest): Promise<SetupMfaResponse> {
    return request<SetupMfaResponse>('/auth/mfa/setup', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  activateMfa(input: ActivateMfaRequest): Promise<ActivateMfaResponse> {
    return request<ActivateMfaResponse>('/auth/mfa/activate', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  verifyMfa(input: VerifyMfaRequest): Promise<VerifyMfaResponse> {
    return request<VerifyMfaResponse>('/auth/mfa/verify', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  // Email OTP — alternative second factor (email-otp-2fa). Lets a user who
  // skipped TOTP enrollment (or has it enabled) verify via a 6-digit code
  // emailed to them instead of an authenticator-app code.
  requestMfaEmailOtp(input: RequestMfaEmailOtpRequest): Promise<RequestMfaEmailOtpResponse> {
    return request<RequestMfaEmailOtpResponse>('/auth/mfa/email/request', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },

  verifyMfaEmailOtp(input: VerifyMfaEmailOtpRequest): Promise<VerifyMfaEmailOtpResponse> {
    return request<VerifyMfaEmailOtpResponse>('/auth/mfa/email/verify', {
      method: 'POST',
      body: input,
      refreshOn401: false,
    });
  },
};
